import type { LocalDatabase } from '../core/types';
import type { WorkoutRecord } from '~/powersync/generated/schema';
import type { WorkoutWithExerciseCount } from './types';
import { withOptionalId } from '../core/utils';
import type { QueryBuilderSpec } from '~/powersync/hooks/useLivePowerSyncQuery';

export const WorkoutQueries = {
  findById: (id: string): QueryBuilderSpec<WorkoutRecord> => ({
    createQuery: (db: LocalDatabase) =>
      db
        .selectFrom('Workout')
        .selectAll()
        .where('id', '=', id)
        .where('deletedAt', 'is', null),
    queryKey: ['workout', 'id', id],
  }),

  findByIds: (ids: string[]): QueryBuilderSpec<WorkoutRecord> => ({
    createQuery: (db: LocalDatabase) =>
      db
        .selectFrom('Workout')
        .selectAll()
        .where('id', 'in', ids)
        .where('deletedAt', 'is', null),
    queryKey: ['workout', 'ids', ...ids],
  }),

  findByUserId: (userId: string): QueryBuilderSpec<WorkoutRecord> => ({
    createQuery: (db: LocalDatabase) =>
      db
        .selectFrom('Workout')
        .selectAll()
        .where('userId', '=', userId)
        .where('deletedAt', 'is', null)
        .orderBy('createdAt', 'desc'),
    queryKey: ['workout', 'userId', userId],
  }),

  findWithExerciseCount: (userId: string): QueryBuilderSpec<unknown> => ({
    createQuery: (db: LocalDatabase) =>
      db
        .selectFrom('Workout')
        .leftJoin('WorkoutSuperset', 'Workout.id', 'WorkoutSuperset.workoutId')
        .leftJoin(
          'WorkoutExercise',
          'WorkoutSuperset.id',
          'WorkoutExercise.supersetId',
        )
        .select([
          'Workout.id',
          'Workout.name',
          'Workout.userId',
          'Workout.niceId',
          'Workout.duration',
          'Workout.startedAt',
          'Workout.finishedAt',
          'Workout.createdAt',
          'Workout.updatedAt',
          'Workout.deletedAt',
          db.fn.countAll<number>().as('exerciseCount'),
        ])
        .where('Workout.userId', '=', userId)
        .where('Workout.deletedAt', 'is', null)
        .where((eb) =>
          eb.or([
            eb('WorkoutExercise.deletedAt', 'is', null),
            eb('WorkoutExercise.id', 'is', null),
          ]),
        )
        .groupBy([
          'Workout.id',
          'Workout.name',
          'Workout.userId',
          'Workout.niceId',
          'Workout.duration',
          'Workout.startedAt',
          'Workout.finishedAt',
          'Workout.createdAt',
          'Workout.updatedAt',
          'Workout.deletedAt',
        ])
        .orderBy('Workout.createdAt', 'desc'),
    queryKey: ['workout', 'with-exercise-count', 'userId', userId],
  }),

  findSupersetsWithExercises: (
    workoutId: string,
  ): QueryBuilderSpec<unknown> => ({
    createQuery: (db: LocalDatabase) =>
      db
        .selectFrom('WorkoutSuperset')
        .selectAll()
        .where('workoutId', '=', workoutId)
        .where('deletedAt', 'is', null)
        .orderBy('sortIndex', 'asc'),
    queryKey: ['workout', 'supersets', workoutId],
  }),

  findSupersetById: (id: string): QueryBuilderSpec<unknown> => ({
    createQuery: (db: LocalDatabase) =>
      db
        .selectFrom('WorkoutSuperset')
        .selectAll()
        .where('id', '=', id)
        .where('deletedAt', 'is', null),
    queryKey: ['workout', 'superset', 'id', id],
  }),

  // WorkoutExercise queries
  findExerciseById: (id: string): QueryBuilderSpec<unknown> => ({
    createQuery: (db: LocalDatabase) =>
      db
        .selectFrom('WorkoutExercise')
        .selectAll()
        .where('id', '=', id)
        .where('deletedAt', 'is', null),
    queryKey: ['workout', 'exercise', 'id', id],
  }),

  findExercisesBySuperset: (supersetId: string): QueryBuilderSpec<unknown> => ({
    createQuery: (db: LocalDatabase) =>
      db
        .selectFrom('WorkoutExercise')
        .selectAll()
        .where('supersetId', '=', supersetId)
        .where('deletedAt', 'is', null)
        .orderBy('sortIndex', 'asc'),
    queryKey: ['workout', 'exercise', 'superset', supersetId],
  }),

  // WorkoutSetValue queries
  findSetValue: (
    setId: string,
    exerciseDimensionId: string,
  ): QueryBuilderSpec<unknown> => ({
    createQuery: (db: LocalDatabase) =>
      db
        .selectFrom('WorkoutSetValue')
        .selectAll()
        .where('setId', '=', setId)
        .where('exerciseDimensionId', '=', exerciseDimensionId),
    queryKey: ['workout', 'set-value', setId, exerciseDimensionId],
  }),

  // Date-based queries
  findByDateRange: (
    fromDate: string,
    toDate: string,
  ): QueryBuilderSpec<unknown> => ({
    createQuery: (db: LocalDatabase) =>
      db
        .selectFrom('Workout')
        .leftJoin('WorkoutSuperset', 'Workout.id', 'WorkoutSuperset.workoutId')
        .leftJoin(
          'WorkoutExercise',
          'WorkoutSuperset.id',
          'WorkoutExercise.supersetId',
        )
        .select([
          'Workout.id',
          'Workout.name',
          'Workout.userId',
          'Workout.niceId',
          'Workout.duration',
          'Workout.startedAt',
          'Workout.finishedAt',
          'Workout.createdAt',
          'Workout.updatedAt',
          'Workout.deletedAt',
          'Workout.clonedFromId',
          'Workout.plannedAt',
          db.fn.count<number>('WorkoutExercise.id').as('exerciseCount'),
        ])
        .where('Workout.plannedAt', '>=', fromDate)
        .where('Workout.plannedAt', '<=', toDate)
        .where('Workout.deletedAt', 'is', null)
        .where((eb) =>
          eb.or([
            eb('WorkoutExercise.deletedAt', 'is', null),
            eb('WorkoutExercise.id', 'is', null),
          ]),
        )
        .groupBy([
          'Workout.id',
          'Workout.name',
          'Workout.userId',
          'Workout.niceId',
          'Workout.duration',
          'Workout.startedAt',
          'Workout.finishedAt',
          'Workout.createdAt',
          'Workout.updatedAt',
          'Workout.deletedAt',
          'Workout.clonedFromId',
          'Workout.plannedAt',
        ]),
    queryKey: ['workout', 'dateRange', fromDate, toDate],
  }),

  findByNiceId: (
    niceId: string | null | undefined,
  ): QueryBuilderSpec<WorkoutRecord> => ({
    createQuery: niceId
      ? (db: LocalDatabase) =>
          db
            .selectFrom('Workout')
            .selectAll()
            .where('niceId', '=', niceId)
            .where('deletedAt', 'is', null)
            .limit(1)
      : null,
    queryKey: ['workout', 'niceId', niceId ?? 'null'],
  }),

  // WorkoutSet queries
  findSetById: (id: string | null | undefined): QueryBuilderSpec<unknown> => ({
    createQuery: id
      ? (db: LocalDatabase) =>
          db.selectFrom('WorkoutSet').selectAll().where('id', '=', id)
      : null,
    queryKey: ['workout', 'set', 'id', id ?? 'null'],
  }),

  findSetsByExercise: (exerciseId: string): QueryBuilderSpec<unknown> => ({
    // Order by the authoritative sortIndex (matching useWorkoutSetSequence /
    // the progress overview), with an id tiebreak for a deterministic order on
    // sortIndex ties — never by createdAt, which ties when sets are cloned from
    // a previous session in a single transaction.
    createQuery: (db: LocalDatabase) =>
      db
        .selectFrom('WorkoutSet')
        .selectAll()
        .where('exerciseId', '=', exerciseId)
        .orderBy('sortIndex', 'asc')
        .orderBy('id', 'asc'),
    queryKey: ['workout', 'sets', 'exercise', exerciseId],
  }),

  // WorkoutExerciseDimension queries
  findDimensionsByExercise: (
    exerciseId: string,
  ): QueryBuilderSpec<unknown> => ({
    createQuery: (db: LocalDatabase) =>
      db
        .selectFrom('WorkoutExerciseDimension')
        .selectAll()
        .where('exerciseId', '=', exerciseId)
        .orderBy('createdAt', 'asc'),
    queryKey: ['workout', 'dimensions', 'exercise', exerciseId],
  }),

  findDimensionsByWorkout: (workoutId: string): QueryBuilderSpec<unknown> => ({
    createQuery: (db: LocalDatabase) =>
      db
        .selectFrom('WorkoutExerciseDimension')
        .selectAll()
        .where('workoutId', '=', workoutId),
    queryKey: ['workout', 'dimensions', 'workout', workoutId],
  }),

  // Aggregate queries across multiple workouts (e.g. weekly stats)
  findDimensionsByWorkoutIds: (
    workoutIds: string[],
  ): QueryBuilderSpec<unknown> => ({
    createQuery: workoutIds.length
      ? (db: LocalDatabase) =>
          db
            .selectFrom('WorkoutExerciseDimension')
            .selectAll()
            .where('workoutId', 'in', workoutIds)
      : null,
    queryKey: ['workout', 'dimensions', 'workoutIds', ...workoutIds],
  }),

  // WorkoutSetValue queries by workout
  findSetValuesByWorkout: (workoutId: string): QueryBuilderSpec<unknown> => ({
    createQuery: (db: LocalDatabase) =>
      db
        .selectFrom('WorkoutSetValue')
        .selectAll()
        .where('workoutId', '=', workoutId),
    queryKey: ['workout', 'set-values', 'workout', workoutId],
  }),

  findSetValuesByWorkoutIds: (
    workoutIds: string[],
  ): QueryBuilderSpec<unknown> => ({
    createQuery: workoutIds.length
      ? (db: LocalDatabase) =>
          db
            .selectFrom('WorkoutSetValue')
            .selectAll()
            .where('workoutId', 'in', workoutIds)
      : null,
    queryKey: ['workout', 'set-values', 'workoutIds', ...workoutIds],
  }),

  // WorkoutSet queries by workout
  findSetsByWorkout: (workoutId: string): QueryBuilderSpec<unknown> => ({
    createQuery: (db: LocalDatabase) =>
      db
        .selectFrom('WorkoutSet')
        .selectAll()
        .where('workoutId', '=', workoutId),
    queryKey: ['workout', 'sets', 'workout', workoutId],
  }),

  findSetsByWorkoutIds: (workoutIds: string[]): QueryBuilderSpec<unknown> => ({
    createQuery: workoutIds.length
      ? (db: LocalDatabase) =>
          db
            .selectFrom('WorkoutSet')
            .selectAll()
            .where('workoutId', 'in', workoutIds)
      : null,
    queryKey: ['workout', 'sets', 'workoutIds', ...workoutIds],
  }),

  // WorkoutExercise queries by workout
  findExercisesByWorkout: (workoutId: string): QueryBuilderSpec<unknown> => ({
    createQuery: (db: LocalDatabase) =>
      db
        .selectFrom('WorkoutExercise')
        .selectAll()
        .where('workoutId', '=', workoutId)
        .where('deletedAt', 'is', null),
    queryKey: ['workout', 'exercises', 'workout', workoutId],
  }),

  findExercisesByWorkoutIds: (
    workoutIds: string[],
  ): QueryBuilderSpec<unknown> => ({
    createQuery: workoutIds.length
      ? (db: LocalDatabase) =>
          db
            .selectFrom('WorkoutExercise')
            .selectAll()
            .where('workoutId', 'in', workoutIds)
            .where('deletedAt', 'is', null)
      : null,
    queryKey: ['workout', 'exercises', 'workoutIds', ...workoutIds],
  }),

  /**
   * The sport activities the user uses most across their workouts, most-frequent
   * first. Counts (non-deleted) `WorkoutExercise` rows grouped by
   * `sportActivityId` — used by the right-rail "favourites" list. Returns
   * `{ sportActivityId, exerciseCount }`; the caller resolves activity details.
   */
  findMostLoggedSportActivities: (limit = 5): QueryBuilderSpec<unknown> => ({
    createQuery: (db: LocalDatabase) =>
      db
        .selectFrom('WorkoutExercise')
        .select((eb) => [
          'WorkoutExercise.sportActivityId',
          eb.fn.count('WorkoutExercise.id').as('exerciseCount'),
        ])
        .where('deletedAt', 'is', null)
        .where('sportActivityId', 'is not', null)
        .groupBy('WorkoutExercise.sportActivityId')
        .orderBy((eb) => eb.fn.count('WorkoutExercise.id'), 'desc')
        .limit(limit),
    queryKey: ['workout', 'sportActivities', 'mostLogged', limit],
  }),
};
